import { PowersearchModule } from '@abovo/powersearch';
import { AssetLoaderModule, ImageFallbackModule } from '@abovo/shared-angular-utils';
import { getCurrentUser } from '@abovo/shared-auth';
import { createLocalStorage, EmployeeImageModule, SidenavService, THEMES, ThemeV2Service, WindowResizeService } from '@abovo/shared-services';
import { BookmarksStore, getRouterState } from '@abovo/webvis-data-access';
import { DrawerComponent, FabButtonsComponent, FabButtonsService, PageHeaderComponent, PageHistoryComponent } from '@abovo/webvis-ui';
import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatSidenav, MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';
import { Router, RouterOutlet } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { TourService, TourStepTemplateComponent } from 'ngx-ui-tour-md-menu';
import { map } from 'rxjs/operators';
import { QuickCreateMenuComponent, useTimeRegistrationDialog } from '../../features';

@Component({
   selector: 'webvis-ui-container',
   templateUrl: './webvis.component.html',
   styleUrls: ['./webvis.component.scss'],
   changeDetection: ChangeDetectionStrategy.OnPush,
   // The room the floating action buttons take at the bottom right. Lists inside the shell keep their last rows clear of them.
   host: { '[style.--fab-padding]': 'fabButtonsService.floatingActionButtonsPadding()' },
   imports: [
      MatSidenavContainer,
      MatSidenav,
      MatSidenavContent,
      RouterOutlet,
      TranslatePipe,
      AssetLoaderModule,
      EmployeeImageModule,
      ImageFallbackModule,
      PageHeaderComponent,
      PowersearchModule,
      FabButtonsComponent,
      PageHistoryComponent,
      DrawerComponent,
      TourStepTemplateComponent,
      FaIconComponent,
      MatButton,
      MatIconButton,
      QuickCreateMenuComponent,
   ],
})
export class WebvisComponent {
   private readonly store = inject(Store);
   readonly router = inject(Router);
   private readonly ls = createLocalStorage<{ tab_drawer_open: boolean }>();
   private readonly windowResize = inject(WindowResizeService);
   private readonly sidenavService = inject(SidenavService);
   private readonly themeService = inject(ThemeV2Service);
   private readonly bookmarksStore = inject(BookmarksStore);
   protected readonly fabButtonsService = inject(FabButtonsService);

   private readonly menuSidenav = viewChild.required<MatSidenav>('sidenav');
   private readonly bookmarkSidenav = viewChild.required<MatSidenav>('bookmarks');

   private readonly timeRegistrationDialog = useTimeRegistrationDialog();

   /** Referenced by the custom tour step template in the shell template. */
   readonly tourService = inject(TourService);

   private sideNavEffect = effect(() => {
      this.sidenavService.menuSidenav = this.menuSidenav();
      this.sidenavService.bookmarkSidenav = this.bookmarkSidenav();
   });

   readonly drawerExpanded = signal(false);
   readonly tabDrawerOpen = signal(this.ls.get('tab_drawer_open') ?? false);
   readonly currentRoute = this.store.selectSignal(getRouterState);
   currentUserNumber = toSignal(this.store.select(getCurrentUser).pipe(map((u) => (!!u && u.reference) || 0)));
   activeScreenSize = toSignal(this.windowResize.activeScreenSize$);
   private currentPageSignal = this.store.selectSignal(getRouterState);

   currentPageIsBookmarked = computed(() => {
      const currentPage = this.currentPageSignal();
      const bookmarks = this.bookmarksStore.bookmarksEntities();
      return !!bookmarks.find((b) => b.slug === currentPage.slug);
   });

   changeTabDrawerOpen(open: boolean) {
      this.tabDrawerOpen.set(open);
      this.ls.set('tab_drawer_open', open);
   }

   toolbarUserIcon = computed(() => {
      const themeName = this.themeService.currentThemeSettings().themeName;
      return THEMES[themeName].userIcon;
   });

   headerToggleBookmark() {
      if (this.currentPageIsBookmarked()) {
         this.bookmarksStore.removeBookmark(this.currentPageSignal());
      } else {
         this.bookmarksStore.addBookmark(this.currentPageSignal());
      }
   }

   openTimeRegistrationDialog() {
      this.timeRegistrationDialog.create();
   }
}
